import {BusEvent, Dispatcher, EventBus} from 'dispatcher'
import {
  DELETE_CHAT_TYPE,
  DIRECT_MESSAGE_SEEN_TYPE,
  DIRECT_MESSAGE_TYPE,
  WS_ACTIVE_CONVERSATION_DATA,
  WS_CHAT_TYPE_VALUE,
  WS_CND_DIRECT_MESSAGE_SEEN_DATA,
  WS_DELETE_CHAT_DATA,
  WS_DIRECT_MESSAGE_HISTORY_DATA,
  WS_DIRECT_MESSAGE_RECEIVE_DATA,
  WS_DIRECT_MESSAGE_SEND_DATA
} from 'ws'
import {WSCommandProcessor} from './cmd/WSCMD'
import {ChatHistoryCommand} from './cmd/HistoryCommand'
import {ActiveCommand} from './cmd/ActiveCommand'
import {SendMessageCommand} from './cmd/SendMessageCommand'
import {ChatMessageDispatcher} from './ChatMessageDispatcher'
import {ChatMessage, ChatMessageSet} from './ChatMessageSet'
import {
  ActiveConversationData,
  AppChatEvent,
  AssignmentId,
  SendMessageData
} from './types'
import {DeleteChatCommand} from './cmd/DeleteChatCommand'

export class ChatService {
  protected conversations: Map<AssignmentId, ChatMessageSet> = new Map()
  protected activeConversations: Map<AssignmentId, ActiveConversationData> =
    new Map()
  protected cmd = new WSCommandProcessor()

  protected constructor(protected eventBus: EventBus) {
    this.configureDispatcher()

    this.eventBus.connection.subscribe((connected) => {
      if (connected) {
        this.loadActiveConversations().then()
      }
    })
  }

  protected createDispatcher(): Dispatcher<
    WS_DIRECT_MESSAGE_RECEIVE_DATA,
    unknown
  > {
    return new ChatMessageDispatcher(this.eventBus)
  }

  protected configureDispatcher() {
    this.createDispatcher().register((event) =>
      this.writeToConversations(
        event.data!.meta.assignment._id,
        this.DDMTOChatMessage(event.data!, true),
        true
      )
    )
  }

  // Send new message event
  protected sendEvent(event: WS_CND_DIRECT_MESSAGE_SEEN_DATA) {
    const wsEvent: BusEvent<WS_CND_DIRECT_MESSAGE_SEEN_DATA, undefined> = {
      type: WS_CHAT_TYPE_VALUE,
      data: event,
      scope: 'WS'
    }
    this.eventBus.fire(wsEvent)
  }

  // TRANSFORM HELPER METHOD
  DDMTOChatMessage(event: AppChatEvent, sent?: boolean): ChatMessage {
    return {
      id: event.data._id,
      chatId: event.meta.assignment._id,
      senderUserId:
        event.meta.sender?.id ?? this.eventBus.credentials?.userId ?? '',
      recipientUserId: event.meta.recipient?.id ?? '',
      message: event.data.text ?? event.data.body,
      date: event.data.ts ?? Date.now(),
      sent: sent,
      metadata: event.data.metadata
    }
  }

  // Load conversation history from ws
  async loadHistory(assignmentId: string) {
    if (this.activeConversations.has(assignmentId)) {
      try {
        const response = (
          await this.cmd.execute<WS_DIRECT_MESSAGE_HISTORY_DATA, unknown>(
            new ChatHistoryCommand(assignmentId, this.eventBus)
          )
        ).data!
        response.data.messages.forEach((entry) => {
          const message = ChatHistoryCommand.HISTORYToChatMessage(
            response.data.id,
            response.data.recipient.id,
            entry
          )
          this.writeToConversations(assignmentId, message)
        })
      } catch (e) {
        console.error(e)
      }
    }
    return this.readFromConversations(assignmentId)
  }

  // Get unseen counter for active conversations
  async loadActiveConversations(
    forceReload: boolean = false
  ): Promise<Map<string, ActiveConversationData>> {
    return new Promise((r) => {
      if (!forceReload && this.activeConversations.size > 0) {
        r(this.activeConversations)
        return
      }
      this.cmd
        .execute<WS_ACTIVE_CONVERSATION_DATA, unknown>(
          new ActiveCommand(this.eventBus)
        )
        .then((rd) => {
          rd.data!.data.chats.forEach((chat) =>
            this.activeConversations.set(chat.id, chat)
          )
          r(this.activeConversations)
        })
        .catch((_) => {
          r(this.activeConversations)
        })
    })
  }

  // Organized messages into conversations
  protected writeToConversations(
    assignmentId: string,
    event: ChatMessage,
    incrementUnseen: boolean = false
  ) {
    const conversation =
      this.conversations.get(assignmentId) ?? new ChatMessageSet()
    conversation.add(event)
    this.conversations.set(assignmentId, conversation)
    this.writeToActiveConversation(assignmentId, event, incrementUnseen)
  }

  protected writeToActiveConversation(
    assignmentId: string,
    event: ChatMessage,
    incrementUnseen: boolean = false
  ) {
    this.activeConversations.set(assignmentId, {
      unseen:
        (this.activeConversations.get(assignmentId)?.unseen ?? 0) +
        (incrementUnseen ? 1 : 0),
      id: event.chatId,
      lastMessage: {
        ts: event.date,
        _id: event.id,
        senderId: event.senderUserId,
        body: event.message
      }
    })
  }

  // READ STORED CONVERSATION
  readFromConversations(assignmentId: AssignmentId): ChatMessage[] {
    return Array.from(
      (this.conversations.get(assignmentId) ?? new ChatMessageSet()).get()
    ).sort((a, b) => a.date - b.date)
  }

  protected prepareMessage(
    message: SendMessageData
  ): WS_DIRECT_MESSAGE_SEND_DATA {
    const data: WS_DIRECT_MESSAGE_SEND_DATA['data'] = {
      _id: this.eventBus.generateUUID(),
      text: message.text,
      metadata: message.metadata
    }
    const meta: WS_DIRECT_MESSAGE_SEND_DATA['meta'] = {
      assignment: {_id: message.assignmentId},
      recipient: {id: message.recipientId}
    }
    return {
      type: DIRECT_MESSAGE_TYPE,
      data: data,
      meta: meta
    }
  }

  // SEND MESSAGE THROUGH WS
  async sendMessage(message: SendMessageData): Promise<ChatMessage> {
    const event = this.prepareMessage(message)

    try {
      const ack = await this.cmd.execute<WS_DIRECT_MESSAGE_SEND_DATA, unknown>(
        new SendMessageCommand(event, this.eventBus)
      )
      const rValue = this.DDMTOChatMessage(ack.data!, true)
      this.writeToConversations(message.assignmentId, rValue)
      return rValue
    } catch (e) {
      // this.writeToConversations(message.assignmentId, rValue)
      return this.DDMTOChatMessage(event, false)
    }
  }

  // MARK LAST MESSAGE AS SEEN
  markMessageAsSeen(assignmentId: string, messageId: string) {
    const event: WS_CND_DIRECT_MESSAGE_SEEN_DATA = {
      type: DIRECT_MESSAGE_SEEN_TYPE,
      data: {
        _id: messageId,
        chatId: assignmentId,
        seen: true
      }
      // meta: undefined,
    }
    const entry = this.activeConversations.get(assignmentId)
    if (entry) {
      entry.unseen = 0
      this.activeConversations.set(assignmentId, entry)
    } else {
      this.activeConversations.set(assignmentId, {
        unseen: 0,
        id: assignmentId
      })
    }
    this.sendEvent(event)
  }

  markMessageAsSeenIfConversationsHasUnseen(
    assignmentId: string,
    messageId: string
  ) {
    const unseen = this.activeConversations.get(assignmentId)?.unseen ?? 0
    if (unseen > 0) {
      this.markMessageAsSeen(assignmentId, messageId)
    }
  }

  async deleteChat(assignmentId: string) {
    if (this.activeConversations.has(assignmentId)) {
      try {
        await this.cmd.execute<WS_DELETE_CHAT_DATA, undefined>(
          new DeleteChatCommand(this.eventBus, assignmentId)
        )
        this.conversations.delete(assignmentId)
        this.activeConversations.delete(assignmentId)
      } catch (e) {
        console.error(e)
      }
    }
  }

  static instance: ChatService

  static shared(eventBus: EventBus) {
    return (
      ChatService.instance ?? (ChatService.instance = new ChatService(eventBus))
    )
  }
}
